<script setup>
import { useI18n } from "vue-i18n";
import QMoon from "@/icons/QMoon.vue";
import QSunrise from "@/icons/QSunrise.vue";

const { t } = useI18n();

const props = defineProps({
    /**
     * Quote data with premarket and afterhours info
     */
    data: {
        type: Object,
        required: true,
    },
    /**
     * Show change direction icon (triangle)
     */
    showChangeIcon: {
        type: Boolean,
        default: false,
    },
    /**
     * Include dollar sign in price display
     */
    showDollarSign: {
        type: Boolean,
        default: false,
    },
});
</script>

<template>
    <div
        class="qmodii-extended-hours"
        v-if="
            (data.tradingSession === 'premarket' && data.premarket) ||
            (data.tradingSession === 'afterhours' && data.afterhours)
        "
    >
        <!-- Premarket data -->
        <template v-if="data.tradingSession === 'premarket' && data.premarket">
            <span class="qmodii-extended-hours-label">{{ t("Pre") }}:</span>
            <span class="qmodii-data-source"><QSunrise /></span>
            <span class="qmodii-extended-hours-price">
                <template v-if="showDollarSign">$</template>{{ data.premarket.last }}
            </span>
            <span
                class="qmodii-extended-hours-change"
                :class="`qmodii-change-${data.premarket.changeDirection}`"
            >
                {{ data.premarket.changeSign }}{{ data.premarket.change }} ({{
                    data.premarket.changeSign
                }}{{ data.premarket.changePercent }})
                <span
                    class="qmodii-dorito"
                    v-if="showChangeIcon && data.premarket.changeDirection !== 'neutral'"
                >
                    {{ data.premarket.changeDirection === "positive" ? "▲" : "▼" }}
                </span>
            </span>
        </template>

        <!-- Afterhours data -->
        <template v-if="data.tradingSession === 'afterhours' && data.afterhours">
            <span class="qmodii-extended-hours-label">{{ t("Post") }}:</span>
            <span class="qmodii-data-source"><QMoon /></span>
            <span class="qmodii-extended-hours-price">
                <template v-if="showDollarSign">$</template>{{ data.afterhours.last }}
            </span>
            <span
                class="qmodii-extended-hours-change"
                :class="`qmodii-change-${data.afterhours.changeDirection}`"
            >
                {{ data.afterhours.changeSign }}{{ data.afterhours.change }} ({{
                    data.afterhours.changeSign
                }}{{ data.afterhours.changePercent }})
                <span
                    class="qmodii-dorito"
                    v-if="showChangeIcon && data.afterhours.changeDirection !== 'neutral'"
                >
                    {{ data.afterhours.changeDirection === "positive" ? "▲" : "▼" }}
                </span>
            </span>
        </template>
    </div>
</template>

<style lang="stylus" scoped>
.qmodii-extended-hours
    display flex
    flex-wrap wrap
    align-items center
    width 100%
    border-top var(--qmodii-primary-border)
    padding-top var(--qmodii-spacing-sm)

    *
        font-size var(--qmodii-text-sm)

    &-label
        margin-right 4px
        white-space nowrap

    &-price
        margin 0 4px 0 2px
        white-space nowrap

    &-change
        white-space nowrap

        &.qmodii-change-positive
            color var(--qmodii-quote-positive-color)

        &.qmodii-change-negative
            color var(--qmodii-quote-negative-color)

.qmodii-data-source
    margin-right 2px
    display inline-flex
    align-items center

.qmodii-dorito
    display inline-block
    margin-left 3px
</style>
